import type { DemoDomain } from './demoCenter'
import { componentDefinitions } from './siteContent'

/** What each domain's demo lets you do, beyond its one-line summary. */
export const domainDemonstrates: Record<DemoDomain['id'], string[]> = {
  government: [
    'Separate Citizen Workspace and Administration Control Panel',
    'Support requests, referrals, identity reviews and appeals with recorded reasons',
    'Reporting from national level down to districts in the fictional Republic of Nordland',
  ],
  research: [
    'Search a synthetic source library and read exact passages',
    'Propose relationships that stay outside approved knowledge until reviewed',
    'Compare studies side by side with their interpretation limits',
  ],
  education: [
    'Fictional adult learners complete a task with visible criteria',
    'Educators record criterion-by-criterion observations and feedback',
    'Practice with reflection, then reassessment in a changed scenario',
  ],
  talent: [
    'The same role criteria applied to two fictional work samples',
    'Reviewer observations, explanations and unanswered questions side by side',
    'A separate human-decision record that requires reviewed evidence and a reason',
  ],
  leadership: [
    'A service-outage scenario with uncertain options',
    'Coach review of trade-offs, communication and accountability',
    'Rehearsal and a follow-up decision when the situation changes',
  ],
  community: [
    'Goal and barrier planning that preserves the participant’s choice',
    'Consent-based, purpose-limited partner referrals',
    'Referral status history visible to the participant',
  ],
  clinical: [
    'One fictional patient across two visits',
    'Patient reports, measurements, observations and interpretations kept distinct',
    'Documentation review, clarification requests and addenda after review',
  ],
}

/** Shared capabilities. These are configurations, not extra domains. */
export const sharedCapabilities = [
  {
    id: 'human-capability-edition',
    name: 'Human Capability Edition',
    body: 'The shared HCF assessment and development configuration used by the Education, Talent, Leadership and Community demos: structured tasks, human review, development activities and reassessment. Where NCJI applies, profiles show its five pillars separately, each with its evidence, review status and interpretation limits.',
  },
  {
    id: 'collaboration',
    name: 'Collaboration',
    body: 'Connects authorized contributions and reviews across domains. Organizations share selected knowledge and coordinate reviews while keeping control of what they share and who can access it.',
  },
]

export const workspacePanels = [
  {
    title: 'Citizen Workspace',
    body: 'Understand your development priorities, explore connected knowledge, practice judgment, manage goals, request services and follow your progress.',
  },
  {
    title: 'Administration Control Panel',
    body: 'Coordinate support delivery, manage program intake, review cases, record decisions and monitor operational activity.',
  },
  {
    title: 'Connected workflows',
    body: 'A support request connects the citizen and administrator views. Requests, additional information, decisions and next steps remain visible throughout the process.',
  },
]

export const separateWorkspacesCopy =
  'The Government demo separates the Citizen Workspace from the Administration Control Panel. Related requests and decisions connect their workflows. In a production implementation, access would be limited by role, purpose and authorization.'

export const demoSwitchNote =
  'The workspace switch demonstrates workflows; it is not a secure account system.'

export const clinicalCurrentScope =
  'The scoped Clinical demo uses one fictional patient and two visits to illustrate structured documentation, clarification, review and addenda.'

export const clinicalFutureOptions = [
  'A session timeline could connect observations to the questions or tasks occurring at that moment.',
  'With appropriate consent, optional recording and compatible equipment could supply audio, video, pupil responses, respiratory rate, temperature or movement measurements.',
]

export const clinicalFutureCaveat =
  'These options are not connected in the demonstration. Each would need separate technical and clinical evaluation, and clinicians would retain responsibility for assessment and diagnosis.'

export const talentExample = [
  {
    requirement: 'Planning',
    demonstrated: 'Created a workable schedule and identified dependencies.',
    followUp: 'Explore their response to a missed milestone.',
  },
  {
    requirement: 'Budget management',
    demonstrated: 'Described tracking project expenses.',
    followUp: 'Verify whether they owned the budget.',
  },
  {
    requirement: 'Handling setbacks',
    demonstrated: 'Explained a recovery plan and its tradeoffs.',
    followUp: 'Clarify their personal contribution.',
  },
]

export const systemFit = [
  { name: componentDefinitions.skp.name, role: componentDefinitions.skp.definition, link: '/semantic-knowledge' },
  { name: componentDefinitions.hcf.name, role: componentDefinitions.hcf.definition, link: '/human-capability' },
  { name: componentDefinitions.noetivra.name, role: componentDefinitions.noetivra.definition, link: null },
]
